import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { Button } from '@repo/design-system-kit';
import { useSegment } from '@repo/poly-hooks/segment';
import type { PropertyOption } from '../../types';
import type { FiltersListItemProps } from './filters-list-item/filters-list-item';
import { FiltersListItem } from './filters-list-item/filters-list-item';
import { FiltersListHeader } from './filters-list-header/filters-list-header';
import { AddFilterButton } from './add-filter-button/add-filter-button';
import styles from './filters-list.module.css';

export const ADVANCED_FILTER_KEY = 'advanced-filter';

const RESET_FILTERS_TRACKING_EVENT = 'reset_filters_clicked';

interface FiltersListProps {
  availableFilters: PropertyOption[];
  pinnedFilters: string[];
  activeFilters?: string[];
  advancedFilterCount?: number;
  hasAdvancedFilters?: boolean;
  onFilterSelect?: (filter: PropertyOption) => void;
  onResetFilters?: () => void;
  children: (filter: PropertyOption, itemProps: FiltersListItemProps) => ReactNode;
}

function FiltersListComponent({
  availableFilters,
  pinnedFilters,
  activeFilters = [],
  advancedFilterCount = 0,
  hasAdvancedFilters = false,
  onFilterSelect,
  onResetFilters,
  children,
}: FiltersListProps) {
  const { formatMessage } = useIntl();
  const segment = useSegment();
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(advancedFilterCount > 0);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [pendingFilter, setPendingFilter] = useState<PropertyOption | null>(null);
  const [activePinnedFilters, setActivePinnedFilters] = useState<string[]>(
    activeFilters.filter(key => pinnedFilters.includes(key))
  );
  const [hasFilterOpen, setHasFilterOpen] = useState(false);

  useEffect(() => {
    setActivePinnedFilters(prev => {
      const removed = prev.filter(f => activeFilters.includes(f));
      const added = !hasFilterOpen
        ? activeFilters.filter(key => pinnedFilters.includes(key) && !prev.includes(key))
        : [];

      const next = [...removed, ...added];

      // Bail out if nothing changed — avoids infinite re-render loop
      if (next.length === prev.length && next.every((v, i) => v === prev[i])) {
        return prev;
      }
      return next;
    });
  }, [activeFilters, hasFilterOpen, pinnedFilters]);

  const shownFilterKeys = new Set([...activeFilters, ...pinnedFilters]);
  const activeFiltersSet = new Set(activeFilters);

  return (
    <div className={styles.container}>
      {showAdvancedFilters ? (
        <Fragment key="advanced-filters">
          {children(
            {
              key: ADVANCED_FILTER_KEY,
              label: '',
            },
            {
              autoOpen: advancedFiltersOpen,
              onOpen: () => {
                setHasFilterOpen(true);
              },
              onClose: () => {
                if (!advancedFilterCount) {
                  setShowAdvancedFilters(false);
                }
              },
            }
          )}
        </Fragment>
      ) : null}

      {activeFilters.flatMap(key => {
        const filter = availableFilters.find(f => f.key === key);
        if (!filter) return [];

        return [
          <Fragment key={filter.key}>
            {children(filter, {
              onOpen: () => {
                setHasFilterOpen(true);
              },
            })}
          </Fragment>,
        ];
      })}

      {pinnedFilters
        .filter(key => !activePinnedFilters.includes(key))
        .flatMap(key => {
          const filter = availableFilters.find(f => f.key === key);
          return filter
            ? [
                <Fragment key={filter.key}>
                  {children(filter, {
                    onOpen: () => {
                      setHasFilterOpen(true);
                    },
                    onClose: () => {
                      if (activeFilters.includes(key)) {
                        setActivePinnedFilters(prev => [...prev, key]);
                      } else {
                        setActivePinnedFilters(prev => prev.filter(k => k !== key));
                      }
                    },
                    isHidden: activeFiltersSet.has(key),
                  })}
                </Fragment>,
              ]
            : [];
        })}

      {pendingFilter
        ? children(pendingFilter, {
            autoOpen: true,
            isHidden: activeFiltersSet.has(pendingFilter.key),
            onOpen: () => {
              setHasFilterOpen(true);
            },
            onClose: () => {
              setPendingFilter(null);
            },
          })
        : null}

      <AddFilterButton
        hasAdvancedFilters={Boolean(hasAdvancedFilters && !advancedFilterCount)}
        onSelect={key => {
          if (key === 'advanced-filter') {
            setAdvancedFiltersOpen(true);
            setShowAdvancedFilters(true);
            return;
          }

          const selectedFilter = availableFilters.find(f => f.key === key);
          setPendingFilter(selectedFilter ?? null);
          if (selectedFilter) {
            onFilterSelect?.(selectedFilter);
          }
        }}
        options={availableFilters
          .filter(filter => !shownFilterKeys.has(filter.key))
          .map(filter => ({
            label: filter.label,
            value: filter.key,
            color: filter.color,
          }))}
      />

      {activeFilters.length > 0 || advancedFilterCount > 0 ? (
        <Button
          className={styles.resetFiltersButton}
          onPress={() => {
            segment.track(RESET_FILTERS_TRACKING_EVENT);
            onResetFilters?.();
          }}
          size="small"
          variant="tertiary"
        >
          {formatMessage({ id: 'shared-kit.filters.reset-filters' })}
        </Button>
      ) : null}
    </div>
  );
}

export const FiltersList = Object.assign(FiltersListComponent, {
  Header: FiltersListHeader,
  ListItem: FiltersListItem,
});
